import React, { useCallback, useEffect, useRef, useState } from "react";
import { CButton, CFormInput } from "@coreui/react";
import { CSpinnerText } from "@tradingdroid/frontends-components";

import { useRunDetail } from "@src/contexts/RunDetailContext";
import { useConfirmModal } from "@src/hooks/useConfirmModal";
import { useToastActions } from "@src/hooks/useToasts";
import { tdApi } from "@src/services/api/tdApi";
import { COMMAND_POLL_MAX_ATTEMPTS, EXECUTION_POLL_DELAY_MS } from "@src/utils/constants";
import { delay } from "@src/utils/dateUtils";

interface Props {
  executionId: number;
  positionId: number;
  positionSize?: number;
  symbol?: string;
}

export const ClosePositionButton: React.FC<Props> = ({
  executionId,
  positionId,
  positionSize,
  symbol,
}) => {
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [note, setNote] = useState("");
  const pollAbortRef = useRef(false);

  // Cleanup on unmount to prevent state updates on unmounted component
  useEffect(() => {
    return () => {
      pollAbortRef.current = true;
    };
  }, []);

  const { showToast } = useToastActions();
  const { confirmModal } = useConfirmModal();
  const { updateRun } = useRunDetail();

  // TODO: Extract this polling logic into a shared useCommandPolling hook.
  // This pattern is repeated in EndExecutionButtons.tsx, OrderCommandForm.tsx, and CancelLimitOrderButton.tsx.
  // The hook should accept: executionId, callbacks (onComplete, onFail), options (maxAttempts, interval),
  // and return: { pollCommand, isPolling }.
  // Poll command status until terminal state
  const pollCommandStatus = useCallback(
    async (commandId: number) => {
      setPolling(true);
      pollAbortRef.current = false;
      let reachedTerminalState = false;

      for (let attempt = 0; attempt < COMMAND_POLL_MAX_ATTEMPTS; attempt++) {
        if (pollAbortRef.current) break;

        await delay(EXECUTION_POLL_DELAY_MS);

        try {
          const command = await tdApi.getExecutionCommand(executionId, commandId);
          if (!command) continue;

          if (command.status === "completed") {
            reachedTerminalState = true;
            const result = command.result as { position_id?: number; movement_id?: number } | null;
            console.log("[ClosePositionButton] Command completed:", {
              commandId,
              executionId,
              positionId,
              result,
              command,
            });
            showToast({
              type: "success",
              title: `Command #${commandId} Executed`,
              message: `Position #${positionId} closed at market`,
              timeout: 8000,
            });

            // Fetch full execution with positions and update Redux
            // This ensures the entire execution state is updated including all position changes
            try {
              const execution = await tdApi.getExecution(executionId, {
                embed: "positions(movements(order))",
              });
              if (execution) {
                updateRun(execution);
                console.log(
                  "[ClosePositionButton] Execution refreshed via context, positions:",
                  execution.positions?.length,
                );
              }
            } catch (err) {
              console.error("[ClosePositionButton] Failed to refresh execution:", err);
            }
            setNote("");
            break;
          } else if (command.status === "failed") {
            reachedTerminalState = true;
            console.log("[ClosePositionButton] Command failed:", {
              commandId,
              executionId,
              positionId,
              error: command.error_message,
              command,
            });
            showToast({
              type: "danger",
              title: `Command #${commandId} Failed`,
              message: command.error_message || "Unknown error",
              timeout: 15000,
            });
            break;
          }
          // Still processing, continue polling
        } catch (err) {
          console.error("[ClosePositionButton] Polling error:", err);
        }
      }

      // Notify user if polling timed out without reaching a terminal state
      if (!reachedTerminalState && !pollAbortRef.current) {
        console.warn("[ClosePositionButton] Polling timed out:", {
          commandId,
          executionId,
          positionId,
          maxAttempts: COMMAND_POLL_MAX_ATTEMPTS,
          totalTimeMs: COMMAND_POLL_MAX_ATTEMPTS * EXECUTION_POLL_DELAY_MS,
        });
        showToast({
          type: "warning",
          title: `Command #${commandId} Timeout`,
          message: `Command is still processing. Check worker logs or try again later.`,
          timeout: 15000,
        });
      }

      if (!pollAbortRef.current) {
        setPolling(false);
      }
    },
    [executionId, positionId, showToast, updateRun],
  );

  const handleClick = async () => {
    // Build confirmation message with amount override option
    const posInfo = symbol ? `#${positionId} (${symbol})` : `#${positionId}`;
    const trackedAmount = positionSize?.toFixed(8) || "unknown";

    // Show confirmation modal with optional amount prompt
    const { confirmed, promptValue } = await confirmModal(
      `Close Position ${posInfo} at market price?\n\nLeave amount empty to use tracked amount (${trackedAmount}).`,
      {
        title: "Market Close Position",
        confirmLabel: "Close Position",
        confirmColor: "danger",
        promptConfig: {
          label: "Amount to close (optional)",
          placeholder: trackedAmount,
          type: "number",
          helpText:
            'TIP: If you get "Insufficient balance", enter the actual balance from exchange.',
        },
      },
    );

    // User cancelled
    if (!confirmed) return;

    // Parse amount override (empty string = use tracked amount)
    const trimmedPrompt = promptValue?.trim() || "";
    const amountOverride = trimmedPrompt ? parseFloat(trimmedPrompt) : undefined;
    if (amountOverride !== undefined && (isNaN(amountOverride) || amountOverride <= 0)) {
      showToast({
        type: "warning",
        title: "Invalid Amount",
        message: "Please enter a valid positive number.",
        timeout: 5000,
      });
      return;
    }

    setLoading(true);
    try {
      // Build payload with optional amount override and note
      const payload: { position_id: number; amount?: number; note?: string } = {
        position_id: positionId,
      };
      if (amountOverride !== undefined) {
        payload.amount = amountOverride;
      }
      if (note.trim()) {
        payload.note = note.trim();
      }

      const command = await tdApi.createExecutionCommand(executionId, {
        command_type_name: "close-position-market",
        payload,
      });

      if (!command?.id) {
        throw new Error("Command created but no ID returned");
      }

      // Build detailed close description
      const closeAmount = amountOverride ?? positionSize;
      const amountLabel = closeAmount ? `${closeAmount} units` : "full position";
      const symbolLabel = symbol ? ` ${symbol}` : "";

      console.log("[ClosePositionButton] Command sent:", {
        commandId: command.id,
        executionId,
        positionId,
        positionSize,
        amountOverride,
        symbol,
        command,
      });
      showToast({
        type: "info",
        title: `Command #${command.id} Sent`,
        message: `Market Close - ${amountLabel}${symbolLabel}`,
        timeout: 5000,
      });

      // Start polling for command status (runs in background)
      pollCommandStatus(command.id);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error("[ClosePositionButton] Failed to send command:", {
        executionId,
        positionId,
        error: err,
      });
      showToast({
        type: "danger",
        title: "Command Failed",
        message: `Failed to send close command: ${message}`,
        timeout: 8000,
      });
    } finally {
      setLoading(false);
    }
  };

  const isDisabled = loading || polling;

  return (
    <div className="ClosePositionButton d-flex flex-column gap-1">
      <CFormInput
        type="text"
        size="sm"
        placeholder="Note (optional)"
        aria-label="Note (optional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        disabled={isDisabled}
        maxLength={500}
      />
      <CButton
        color="danger"
        size="sm"
        variant="outline"
        onClick={handleClick}
        disabled={isDisabled}
        className="py-0 px-2"
      >
        {loading ? (
          <CSpinnerText text="Sending..." />
        ) : polling ? (
          <CSpinnerText text="Closing..." />
        ) : (
          "Market Close"
        )}
      </CButton>
    </div>
  );
};
